import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { GET_MY_OBJECTS_QUERY } from '../../gql';
import { useSettings } from '../context/SettingsContext/useSettings';
import { useWorkspace } from '../context/WorkspaceContext/useWorkspace';
import { OBJECT_DRAW_TOOLS, MapTools } from '../context/WorkspaceContext';
import { BatchEditForm } from './BatchEditForm';
import { ObjectForm } from './ObjectForm';
import { ObjectList } from './ObjectList';
import { Toolchain } from './Toolchain';
import { FormattedMessage } from 'react-intl';
import { Button, Switch, SwitchLabel } from '../design-system';

const labelStyles = { m: 0 };
const labelSlotProps = { typography: { fontSize: 14 } };

const ObjectToolbox = () => {
  const navigate = useNavigate();
  const {
    store: { objectLayerEnabled },
    toggleObjectLayer,
  } = useSettings();
  const {
    store: { objectDraft, editingObjectId, activeMapTool, batchSelectedIds },
  } = useWorkspace();

  const { data, loading } = useQuery(GET_MY_OBJECTS_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !objectLayerEnabled,
  });

  const objects = data?.getMyObjects ?? [];

  const existing = useMemo(() => {
    if (!editingObjectId) return null;
    return objects.find((o: any) => o.id === editingObjectId) ?? null;
  }, [editingObjectId, objects]);

  const batchActive = batchSelectedIds.length > 0;

  const selectedObjects = useMemo(() => {
    if (!batchActive) return [];
    const idSet = new Set(batchSelectedIds);
    return objects.filter((o: any) => idSet.has(o.id));
  }, [batchActive, batchSelectedIds, objects]);

  const formVisible =
    !batchActive &&
    objectLayerEnabled &&
    (!!objectDraft ||
      !!editingObjectId ||
      OBJECT_DRAW_TOOLS.has(activeMapTool as MapTools));

  const handleViewAll = useCallback(() => {
    navigate('/o');
  }, [navigate]);

  return (
    <Stack gap={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SwitchLabel
          value="Display objects"
          sx={labelStyles}
          control={
            <Switch
              size="small"
              checked={objectLayerEnabled}
              onChange={toggleObjectLayer}
              tone="primary"
            />
          }
          label={
            <FormattedMessage
              id="map.layer.objects"
              defaultMessage="Object layer"
            />
          }
          labelPlacement="start"
          slotProps={labelSlotProps}
        />
        <Button onClick={handleViewAll} tone="neutral">
          <FormattedMessage id="map.viewAll" defaultMessage="View all" />
        </Button>
      </Stack>

      <Toolchain disabled={!objectLayerEnabled} />

      {batchActive && objectLayerEnabled && (
        <Stack sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage
              id="map.objects.selectedCount"
              defaultMessage="{count, plural, one {# object selected} other {# objects selected}}"
              values={{ count: selectedObjects.length }}
            />
          </Typography>
          <BatchEditForm selectedObjects={selectedObjects} />
        </Stack>
      )}

      {formVisible && <ObjectForm existing={existing} />}

      {objectLayerEnabled && <ObjectList objects={objects} loading={loading} />}
    </Stack>
  );
};

export { ObjectToolbox };
